<template>
  <v-card color="#05182d" class="pb-2 chart-card">
    <v-list-item class="chart-header">
      <v-list-item-title class="chart-title">{{ title }}</v-list-item-title>
      <v-spacer></v-spacer>
      <v-list-item-action>
        <date-picker v-model="tanggal" @input="onDateInput"></date-picker>
      </v-list-item-action>
      <v-list-item-action>
        <date-picker v-model="tanggal2" @input="onDateInput"></date-picker>
      </v-list-item-action>
    </v-list-item>

    <v-divider class="cyan-divider"></v-divider>

    <div class="chart-container">
      <scatter
        :chart-data="chartdata"
        :options="options"
        :height="height"
      ></scatter>
    </div>

    <v-list-item-title class="chart-footer text-caption px-10">
      {{ footer }}&nbsp;
    </v-list-item-title>
  </v-card>
</template>

<script>
import Scatter from "@/components/Scatter.js";
import DatePicker from "@/components/Editor/DatePicker.vue";

// Konfigurasi X-Axes yang disederhanakan dari versi sebelumnya
const xAxesConfig = {
  type: "time",
  time: {
    unit: "day",
    displayFormats: { day: "YYYY-MM-DD" }, 
    tooltipFormat: "YYYY-MM-DD",
  },
  scaleLabel: {
    labelString: "Tanggal",
  },
  ticks: {
    fontColor: "#00eaff",
  },
  gridLines: {
    color: "rgba(0, 255, 255, 0.2)",
    zeroLineColor: "#00ffff",
  },
};

export default {
  components: { Scatter, DatePicker },
  props: {
    date: { type: String, default: "" }, 
    date2: { type: String, default: "" }, 
    title: { type: String, default: "Title" },
    datasets: { type: Array, default: () => [{ x: 0, y: 1 }] },
    headers: { type: Array, default: () => [] },
    labels: { type: Array, default: () => [] },
    footer: { type: String, default: "" },
    height: { type: Number, default: 250 },
    yscales: { 
        type: Array, 
        default: () => [],
        validator(value) { 
            return Array.isArray(value);
        }
    },
  },
  data() {
    return {
      tanggal: "",
      tanggal2: "",
      colors: [
        "#00eaff",
        "#3a86ff",
        "#ff006e",
        "#2a9d8f",
        "#e9c46a",
        "#8338ec",
        "#f4a261",
      ],
    };
  },
  mounted() {
    this.tanggal = this.date;
    this.tanggal2 = this.date2;
  },
  computed: {
    chartdata() {
      const datasets = [];
      for (let i = 1; i < this.headers.length; i++) {
        const item = this.headers[i];
        const idx = i % this.colors.length;
        datasets.push({
          label: item.caption,
          borderColor: this.colors[idx],
          backgroundColor: this.hexToRgba(this.colors[idx], 0.15),
          fill: true,
          tension: 0.35,
          pointRadius: 2,
          borderWidth: 2,
          showLine: true, // DIJAGA: Untuk memastikan garis muncul
          yAxisID: item["unit"], // DIJAGA: Untuk fungsionalitas y-axis multi-unit
          data: this.chdata[i - 1],
        });
      }
      return { datasets };
    },
    chdata() {
      const result = [];
      if (this.headers.length > 1) {
        const xaxis = this.headers[0];
        const xfield = xaxis.field;
        const istime = xaxis.unit === "time";
        for (let i = 1; i < this.headers.length; i++) {
          const rows = [];
          const yfield = this.headers[i].field;
          for (let j = 0; j < this.datasets.length; j++) {
            const dsrow = this.datasets[j];
            let xvalue = dsrow[xfield];
            if (istime) xvalue = new Date(xvalue).getTime();
            rows.push({ x: xvalue, y: dsrow[yfield] });
          }
          result.push(rows);
        }
      }
      return result;
    },
    options() {
        let result = {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                labels: { fontColor: "#00eaff" },
            },
            scales: {
                xAxes: [xAxesConfig],
                yAxes: [
                  { 
                    gridLines: {
                        color: "rgba(0, 255, 255, 0.2)",
                        zeroLineColor: "#00ffff",
                    },
                    ticks: {
                        fontColor: "#00ffff",
                    },
                  }
                ] 
            },
            tooltips: {
                mode: "index",
                intersect: false,
                backgroundColor: "rgba(5, 24, 45, 0.9)",
                titleFontColor: "#00eaff",
                bodyFontColor: "#ffffff",
                borderColor: "#00eaff",
                borderWidth: 1,
            },
        };
        
        // Mempertahankan logika penambahan yscales dari prop (menimpa default yAxes)
        if (this.yscales && this.yscales.length > 0) {
            // Gabungkan default Y-axis style dengan yscales dari prop
            result.scales.yAxes = this.yscales.map(scale => {
                return {
                    ...scale,
                    gridLines: {
                        color: "rgba(0, 255, 255, 0.2)",
                        zeroLineColor: "#00ffff",
                    },
                    ticks: {
                        fontColor: "#00ffff",
                    },
                };
            });
        } else if (this.headers.length > 1) {
            // Jika yscales kosong, tetapi ada headers (multi-series), 
            // kita harus memastikan yAxes dibuat berdasarkan unit di headers
            const yAxisConfig = {};
            for (let i = 1; i < this.headers.length; i++) {
                const unit = this.headers[i].unit;
                if (unit && !yAxisConfig[unit]) {
                    yAxisConfig[unit] = {
                        id: unit,
                        position: 'left', // Default position
                        scaleLabel: {
                            display: true,
                            labelString: unit,
                            fontColor: "#00eaff",
                        },
                        gridLines: {
                            color: "rgba(0, 255, 255, 0.2)",
                            zeroLineColor: "#00ffff",
                        },
                        ticks: {
                            fontColor: "#00ffff",
                        },
                    };
                }
            }
            result.scales.yAxes = Object.values(yAxisConfig);
        }
        
        return result;
    },
  },
  methods: {
    onDateInput() {
      this.$emit("datechanged", this.tanggal, this.tanggal2);
    },
    hexToRgba(hex, alpha) {
      const bigint = parseInt(hex.replace("#", ""), 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style scoped>
.v-card {
  background: rgba(15, 25, 35, 0.65) !important;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 234, 255, 0.2) !important;
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 234, 255, 0.08);
  color: #e0f7fa !important;
}

.chart-card {
  backdrop-filter: blur(18px);
  background: rgba(15, 35, 70, 0.6);
  border: 1px solid rgba(0, 255, 255, 0.3);
  box-shadow: 0 0 25px rgba(0, 200, 255, 0.15);
  border-radius: 12px;
  transition: all 0.3s ease;
  height: 100%;
}

.chart-header {
  background-color: rgba(15, 35, 70, 0.7);
  color: #00ffff;
}

.chart-title {
  font-weight: 700;
  color: #00eaff;
}

.chart-footer {
  color: #00eaff;
  opacity: 0.8;
  text-align: center;
}

.chart-container {
  background: radial-gradient(
    circle at top,
    rgba(0, 255, 255, 0.08),
    transparent 80%
  );
  border-radius: 10px;
  padding: 8px;
}

.cyan-divider {
  border-color: #00ffff !important;
}
</style>